import React, { useEffect, useState } from 'react';
import axiosInstance from '../axiosInstance';
import { Button, Box, Typography, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AppBarHeader from './AppBarHeader';
import { jwtDecode } from 'jwt-decode';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import ActivityFeed from './social/ActivityFeed';

import { Card, CardContent, Grid, Paper } from '@mui/material';

import pacmanIcon from '../Icons/pacman.png';
import consoleIcon from '../Icons/console.png';
import legoIcon from '../Icons/lego.webp';
import movieIcon from '../Icons/movie.svg';
import musicIcon from '../Icons/music.svg';
import boardGamesIcon from '../Icons/boardgames.png';
import comicsIcon from '../Icons/Comic.png';
import cardsIcon from '../Icons/Cards.png';
import mangaIcon from '../Icons/manga.png';
import booksIcon from '../Icons/books.png';

import collectionsGif from './banners/Collections.gif';
import wishlistsGif from './banners/Wish-Lists.gif';

const DEFAULT_ORDER = ['games', 'consoles', 'lego', 'movies', 'music', 'books', 'board-games', 'trading-cards', 'manga', 'comics'];

// Static config per collection type
const COLLECTION_CONFIG = {
  games: {
    label: 'Games', hiddenKey: 'Games Collection', featureFlag: null,
    collectionRoute: '/games',         wishlistRoute: '/wishlist/games',
    collectionDesc: 'View and manage your entire video game collection.',
    wishlistDesc:   'Keep track of the games you want to add to your collection.',
    defaultIconSrc: pacmanIcon,        defaultIconEl: null,
  },
  consoles: {
    label: 'Consoles', hiddenKey: 'Console Collection', featureFlag: null,
    collectionRoute: '/consoles',      wishlistRoute: '/wishlist/consoles',
    collectionDesc: 'View and manage your console collection.',
    wishlistDesc:   'Keep track of the consoles you want to add to your collection.',
    defaultIconSrc: consoleIcon,       defaultIconEl: null,
  },
  lego: {
    label: 'LEGO', hiddenKey: 'LEGO Collection', featureFlag: null,
    collectionRoute: '/lego',          wishlistRoute: '/wishlist/legos',
    collectionDesc: 'View and manage your LEGO set collection.',
    wishlistDesc:   'Keep track of the LEGO sets you want to add to your collection.',
    defaultIconSrc: legoIcon,          defaultIconEl: null,
  },
  movies: {
    label: 'Movies', hiddenKey: 'Movie Collection', featureFlag: 'enableMoviesFeature',
    collectionRoute: '/movies',        wishlistRoute: '/wishlist/movies',
    collectionDesc: 'View and manage your movie and DVD collection.',
    wishlistDesc:   'Keep track of the movies you want to add to your collection.',
    defaultIconSrc: movieIcon,         defaultIconEl: null,
  },
  music: {
    label: 'Music', hiddenKey: 'Music Collection', featureFlag: 'enableMusicFeature',
    collectionRoute: '/music',         wishlistRoute: '/wishlist/music',
    collectionDesc: 'View and manage your music, vinyl, and CD collection.',
    wishlistDesc:   'Keep track of the music items you want to add to your collection.',
    defaultIconSrc: musicIcon,         defaultIconEl: null,
  },
  books: {
    label: 'Books', hiddenKey: 'Book Collection', featureFlag: 'enableBooksFeature',
    collectionRoute: '/books',         wishlistRoute: '/wishlist/books',
    collectionDesc: 'View and manage your book collection.',
    wishlistDesc:   'Keep track of the books you want to add to your collection.',
    defaultIconSrc: booksIcon,         defaultIconEl: null,
  },
  'board-games': {
    label: 'Board Games', hiddenKey: 'Board Game Collection', featureFlag: 'enableBoardGamesFeature',
    collectionRoute: '/board-games',   wishlistRoute: '/wishlist/board-games',
    collectionDesc: 'View and manage your board game collection.',
    wishlistDesc:   'Keep track of the board games you want to add to your collection.',
    defaultIconSrc: boardGamesIcon,    defaultIconEl: null,
  },
  'trading-cards': {
    label: 'Trading Cards', hiddenKey: 'Trading Cards Collection', featureFlag: 'enableTradingCardsFeature',
    collectionRoute: '/trading-cards', wishlistRoute: '/wishlist/trading-cards',
    collectionDesc: 'View and manage your trading card collection.',
    wishlistDesc:   'Keep track of the cards you want to add to your collection.',
    defaultIconSrc: cardsIcon,         defaultIconEl: null,
  },
  manga: {
    label: 'Manga', hiddenKey: 'Manga Collection', featureFlag: 'enableMangaFeature',
    collectionRoute: '/manga',         wishlistRoute: '/wishlist/manga',
    collectionDesc: 'View and manage your manga collection.',
    wishlistDesc:   'Keep track of the manga you want to add to your collection.',
    defaultIconSrc: mangaIcon,         defaultIconEl: null,
  },
  comics: {
    label: 'Comics', hiddenKey: 'Comics Collection', featureFlag: 'enableComicsFeature',
    collectionRoute: '/comics',        wishlistRoute: '/wishlist/comics',
    collectionDesc: 'View and manage your comics collection.',
    wishlistDesc:   'Keep track of the comics you want to add to your collection.',
    defaultIconSrc: comicsIcon,        defaultIconEl: null,
  },
};

function mergeOrder(stored, defaults) {
  if (!stored || !stored.length) return defaults;
  const extra = defaults.filter(k => !stored.includes(k));
  return [...stored, ...extra];
}

const CARD_SX = {
  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
  '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 14px 28px rgba(0,0,0,0.35)' },
  background: 'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 4,
  backdropFilter: 'blur(8px)',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  cursor: 'pointer',
};

function CollectionCard({ cfg, collectionKey, route, title, description, collectionSettings, onClick }) {
  const iconSettings = collectionSettings?.[collectionKey]?.icon;
  let iconEl;
  if (iconSettings?.source === 'custom' && iconSettings?.value) {
    iconEl = <img src={`/uploads/${iconSettings.value}`} alt={cfg.label} style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }} />;
  } else if (cfg.defaultIconSrc) {
    iconEl = <img src={cfg.defaultIconSrc} alt={cfg.label} style={{ width: 60, height: 60, objectFit: 'contain' }} />;
  } else {
    iconEl = cfg.defaultIconEl;
  }

  return (
    <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
      <Card onClick={onClick} sx={CARD_SX}>
        <CardContent sx={{ textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>{iconEl}</Box>
          <Typography variant="h5" component="div" gutterBottom>{title}</Typography>
          <Typography variant="body2" color="textSecondary">{description}</Typography>
        </CardContent>
      </Card>
    </Grid>
  );
}

function LandingPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [user, setUser] = useState(null);
  const userId = user ? user.id : null;
  const { featureFlags } = useFeatureFlags();
  const { preferences } = useUserPreferences();

  useEffect(() => {
    if (token) {
      axiosInstance.get(`/users/${jwtDecode(token).id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => setUser(r.data))
        .catch(err => console.error('Error fetching user data:', err));
    }
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  if (!user) return <div>Loading...</div>;

  const orderedKeys = mergeOrder(preferences?.collectionOrder, DEFAULT_ORDER);
  const collectionSettings = preferences?.collectionSettings || {};

  const visibleCollections = orderedKeys.filter(key => {
    const cfg = COLLECTION_CONFIG[key];
    if (!cfg) return false;
    if (user?.hiddenButtons?.includes(cfg.hiddenKey)) return false;
    if (cfg.featureFlag && !featureFlags?.[cfg.featureFlag]) return false;
    return true;
  });

  return (
    <div>
      <AppBarHeader isAdmin={user.isAdmin} userId={user.id} onLogout={handleLogout} />
      <Box sx={{
        minHeight: '100vh',
        py: { xs: 2, sm: 4 },
        background: 'linear-gradient(135deg, #0F1124 0%, #171935 60%, #101226 100%)'
      }}>
        <Container maxWidth="lg">
          {/* Hero */}
          <Paper elevation={0} sx={{
            mb: 4, p: { xs: 2, sm: 3 }, borderRadius: 4,
            background: 'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))',
            backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.12)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h5" sx={{ color: '#E6EAF2', mb: 0.5, fontWeight: 700 }}>
                  Welcome back{user?.name ? `, ${user.name}` : ''}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(230,234,242,0.8)' }}>
                  Jump into your collections or wishlists below.
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Collections */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <img src={collectionsGif} alt="Collections" style={{ width: 220, height: 'auto', borderRadius: 12 }} />
          </Box>
          <Grid container spacing={3} justifyContent="center">
            {visibleCollections.map(key => {
              const cfg = COLLECTION_CONFIG[key];
              return (
                <CollectionCard
                  key={key}
                  cfg={cfg}
                  collectionKey={key}
                  route={cfg.collectionRoute}
                  title={`${cfg.label} Collection`}
                  description={cfg.collectionDesc}
                  collectionSettings={collectionSettings}
                  onClick={() => navigate(cfg.collectionRoute)}
                />
              );
            })}
          </Grid>

          {/* Wishlists */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6, mb: 2 }}>
            <img src={wishlistsGif} alt="Wishlists" style={{ width: 220, height: 'auto', borderRadius: 12 }} />
          </Box>
          <Grid container spacing={3} justifyContent="center">
            {visibleCollections.map(key => {
              const cfg = COLLECTION_CONFIG[key];
              return (
                <CollectionCard
                  key={key}
                  cfg={cfg}
                  collectionKey={key}
                  route={cfg.wishlistRoute}
                  title={`${cfg.label} Wish List`}
                  description={cfg.wishlistDesc}
                  collectionSettings={collectionSettings}
                  onClick={() => navigate(cfg.wishlistRoute)}
                />
              );
            })}
          </Grid>
        </Container>

        {/* Following feed */}
        {featureFlags?.enableActivityFeed && featureFlags?.enableFollowSystem && (
          <Container maxWidth="md" sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ color: '#E6EAF2', mb: 1 }}>Following Feed</Typography>
            <ActivityFeed currentUserId={userId} />
          </Container>
        )}
      </Box>
    </div>
  );
}

export default LandingPage;
